import { type ComponentType } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Alert, Stack, Typography } from '@mui/material';
import { MarketplaceConfigFormData } from '../../schema';
import FormFieldStack from '../atoms/FormFieldStack';
import Switch from '../atoms/Switch';
import FormSelect from '../molecules/FormSelect';
import CollapsibleSection from '../atoms/CollapsibleSection';

const MoralisConfigFields = () => {
  const { control } = useFormContext<MarketplaceConfigFormData>();
  return (
    <Stack spacing={1}>
      <Typography variant='body2'>USDT BEP20 deposits and withdrawals through Moralis.</Typography>
      <FormFieldStack label='Allow deposits'>
        <Controller
          name='moralis.depositEnabled'
          control={control}
          render={({ field }) => (
            <Switch {...field} ref={null} slotProps={{ input: { 'aria-label': 'Moralis deposits' } }} />
          )}
        />
      </FormFieldStack>
      <FormFieldStack label='Allow withdrawals'>
        <Controller
          name='moralis.withdrawalEnabled'
          control={control}
          render={({ field }) => (
            <Switch {...field} ref={null} slotProps={{ input: { 'aria-label': 'Moralis withdrawals' } }} />
          )}
        />
      </FormFieldStack>
    </Stack>
  );
};

const ZeroXProcessingConfigFields = () => {
  const { control } = useFormContext<MarketplaceConfigFormData>();
  const enabled = useWatch({ control, name: 'zeroXProcessing.enabled' });
  return (
    <Stack spacing={1}>
      <FormFieldStack label='Enable 0xProcessing'>
        <Controller
          name='zeroXProcessing.enabled'
          control={control}
          render={({ field }) => (
            <Switch {...field} ref={null} slotProps={{ input: { 'aria-label': 'Enable 0xProcessing' } }} />
          )}
        />
      </FormFieldStack>
      <FormFieldStack label='Allow deposits'>
        <Controller
          name='zeroXProcessing.depositEnabled'
          control={control}
          render={({ field }) => (
            <Switch
              {...field}
              ref={null}
              disabled={!enabled}
              slotProps={{ input: { 'aria-label': '0xProcessing deposits' } }}
            />
          )}
        />
      </FormFieldStack>
      <FormFieldStack label='Allow withdrawals'>
        <Controller
          name='zeroXProcessing.withdrawalEnabled'
          control={control}
          render={({ field }) => (
            <Switch
              {...field}
              ref={null}
              disabled={!enabled}
              slotProps={{ input: { 'aria-label': '0xProcessing withdrawals' } }}
            />
          )}
        />
      </FormFieldStack>
      {!enabled && (
        <Typography variant='body2' color='text.secondary'>
          Deposit and withdrawal choices are retained, but no new 0xProcessing requests can start while the gateway is
          disabled.
        </Typography>
      )}
    </Stack>
  );
};

const PROVIDER_FIELDS = {
  moralis: MoralisConfigFields,
  zero_x_processing: ZeroXProcessingConfigFields,
} satisfies Record<MarketplaceConfigFormData['cryptoProvider'], ComponentType>;

const CryptoGatewayConfig = () => {
  const { control, formState } = useFormContext<MarketplaceConfigFormData>();
  const provider = useWatch({ control, name: 'cryptoProvider' });
  const ProviderFields = PROVIDER_FIELDS[provider];

  return (
    <CollapsibleSection title='Crypto Gateway' defaultOpen>
      <FormSelect
        label='Crypto provider'
        name='cryptoProvider'
        control={control}
        options={[
          { id: 'moralis', name: 'Moralis' },
          { id: 'zero_x_processing', name: '0xProcessing' },
        ]}
        required
      />
      {provider !== (formState.defaultValues?.cryptoProvider ?? 'moralis') && (
        <Alert severity='warning'>
          Changing crypto providers does not transfer funds or existing deposit addresses. Reconcile pending deposits,
          withdrawals and funds held by the current provider before saving. Switching to 0xProcessing also makes Moralis
          withdrawals unavailable. Existing requests remain with their original provider.
        </Alert>
      )}
      <ProviderFields />
    </CollapsibleSection>
  );
};

export default CryptoGatewayConfig;
